import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { useMode } from "../panel/mode";
import { withContext } from "../routes/registry";
import { useSettings, useT } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { isRegistered } from "./participant";
import { firstStep } from "./thread";
import { useOffer } from "./useOffre";
import { useParticipation } from "./useParticipation";

/**
 * ═══ UNE ÉTAPE DU TUNNEL SERVIE COMME PAGE : ELLE CORRIGE, ELLE N'INSCRIT PAS ═══
 *
 * « Mes journées » dans le menu ouvre le même écran que l'étape, avec un bouton qui
 * enregistre. Encore faut-il qu'il y ait une inscription à corriger.
 *
 * ⚠️ SANS CE GARDE, LA PAGE INSCRIVAIT UN REFUS SANS LE DIRE. L'écriture pose
 * `responded: true` et déduit la participation de ce qu'elle trouve : rien dans l'état,
 * rien sur le serveur, et `saveParticipation` conclut « ne vient pas » (voir la note sur
 * la désinscription accidentelle dans `funnel/participant.ts`). Quelqu'un qui n'a jamais
 * répondu, cochant ses journées depuis le menu, se serait donc enregistré comme NE
 * PARTICIPANT PAS. Dans le tunnel la question ne se pose pas : on n'atteint pas les
 * dates sans avoir traversé la participation, le châssis y veille.
 *
 * ⚠️ ET C'EST BIEN LE TUNNEL QUI INSCRIT, PAS CES PAGES. Elles servent une étape hors de
 * son ordre : la traverser seule sauterait tout ce que les autres collectent. On renvoie
 * donc au parcours, à sa première étape servie.
 *
 * ⚠️ RIEN N'EST AFFIRMÉ TANT QU'ON NE SAIT PAS. Annoncer « vous n'êtes pas inscrit »
 * pendant que la question est en vol, c'est le dire à quelqu'un qui l'est : c'est le
 * défaut que l'écran badge a déjà payé, et il se répare du même côté, en attendant la
 * réponse.
 *
 * ⚠️ « A RÉPONDU » NE SUFFIT PAS : UN REFUS A RÉPONDU AUSSI (bug #522). La garde ouvrait
 * « Mes journées » à quelqu'un qui ne vient pas, et « Enregistrer » l'y inscrivait à
 * l'étape près. Elle demande donc une inscription, sauf à la page « Ma participation »
 * (`answered`) : c'est par elle qu'un refus revient, et elle le renvoie au parcours
 * plutôt que d'écrire (voir `routes/Registration.tsx`).
 *
 * Les textes affichés sont français : c'est un produit français.
 */
export function StepPage({
  children,
  answered = false,
}: {
  children: ReactNode;
  /** Une réponse enregistrée suffit, même un refus : la page de participation. */
  answered?: boolean;
}) {
  const t = useT();
  const { recorded, settled, unavailable } = useParticipation();
  const { value } = useSettings();
  const offer = useOffer();
  const navigate = useNavigate();
  const event = useEvent();
  const mode = useMode();
  const first = firstStep(value, offer);

  /* L'organisateur ouvre cet écran depuis le sélecteur du panneau, pour le régler : il
     n'a pas d'inscription à cet événement, et lui opposer ce garde rendrait la page
     impossible à configurer. */
  if (mode !== "visitor") return <>{children}</>;

  if (!settled) {
    return (
      <section className="page">
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_YOUR_REGISTRATION", 'Chargement de votre inscription')}
          </span>
        </p>
      </section>
    );
  }

  /* Identité illisible : ni l'écran, dont le bouton n'écrirait rien, ni le renvoi vers
     l'inscription, qui mentirait à un inscrit dont on n'a pas pu relire la session. */
  if (unavailable) {
    return (
      <section className="page">
        <div className="etat">
          <span className="etat__i" aria-hidden="true">
            <Icon name="verrou" />
          </span>
          <span>
            <b>{t("REGISTRATION_NOT_LOADED", "Votre inscription n'a pas pu être chargée")}</b>
            <small>
              {t(
                "SESSION_UNREADABLE",
                "Votre session n'a pas pu être relue. Rechargez la page ; si cela persiste, prévenez l'organisateur.",
              )}
            </small>
          </span>
        </div>
      </section>
    );
  }

  if (answered ? recorded?.responded === true : isRegistered(recorded))
    return <>{children}</>;

  return (
    <section className="page">
      <div className="etat">
        <span className="etat__i" aria-hidden="true">
          <Icon name="verrou" />
        </span>
        <span>
          <b>{t("NOT_REGISTERED_YET", "Vous n'êtes pas encore inscrit")}</b>
          <small>
            {t(
              "REGISTER_FIRST_THEN_EDIT",
              "Cette page sert à revenir sur une inscription enregistrée. Répondez d'abord au formulaire d'inscription : vous pourrez la corriger ici ensuite.",
            )}
          </small>
        </span>
      </div>

      {first && (
        <div className="actions">
          <Button
            type="button"
            onClick={() => navigate(withContext(first.path, event?.token))}
          >
            {t("REGISTER_ME", "M'inscrire")}
          </Button>
        </div>
      )}
    </section>
  );
}
